import React, { ReactElement, useState, useEffect } from "react";
import { Box, Button, ButtonBase, Typography, TextField, FormControl, Paper, Divider, Fade, IconButton, InputAdornment, MenuItem, FormControlLabel, Checkbox } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import BeaconCategory from "../../../../components/BeaconCategory";
import { Category } from "../../../../types/models/Category";
import { getImageURLCategoryListIcon } from "../../../../utils/utils";
import { MuiColorInput } from "mui-color-input";
import { deleteCategory, getCategories, makeCategory, uploadCategoryImage } from "../../../../redux/actions/Category";
import { fetchError, showMessage } from "../../../../redux/actions";
import AppConfirmDialog from "../../../../components/AppConfirmDialog";
import Skeleton from "react-loading-skeleton";
import { AppColor } from "../../../../types/Constants";
import { MakeCategory } from "../../../../types/models/MakeCategory";
import ImageLoader from "../../../../components/ImageLoader";
import { ClearIcon } from "@mui/x-date-pickers/icons";
import HorizontalMenu from "../../components/creatingBeacon/HorizontalMenu";

type CategoryProps = {
    indexLoading: boolean;
};

const Categories = ({indexLoading}: CategoryProps) => {

    const dispatch = useAppDispatch();

    const categoryGroupState = useAppSelector(state => state.category);
    const businessGroupState = useAppSelector(state => state.businessGroup);
    
    const [buttons, setButtons] = useState<ReactElement[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<Category>();
    const [categories, setCategories] = useState<Category[]>(); 
    const [isCreatingNew, setIsCreatingNew] = useState(false);

    const [name, setName] = useState("");
    const [nameError, setNameError] = useState("");
    const [themeColor, setThemeColor] = useState("#c5c5c5");
    const [theme, setTheme] = useState("");
    const [altitude, setAltitude] = useState("");
    const [maxDistance, setMaxDistance] = useState<number | null>(null);
    const [minDistance, setMinDistance] = useState<number | null>(null);
    const [showDistance, setShowDistance] = useState<boolean>(true);

    const nameLimit = 20;

    const [initialValues, setInitialValues] = useState<{
        name: string;
        themeColor: string;
        maxDistance: number | null;
        minDistance: number | null; 
        theme: string;
        altitude: string;
        showDistance: boolean;
    }>({
        name: "",
        themeColor: "",
        maxDistance: null,
        minDistance: null,
        theme: "ball",
        altitude: "High",
        showDistance: true,
    });

    const [currentFilterFile, setCurrentFilterFile] = useState<File | null>(null);
    const [filterError, setFilterError] = useState<string>("");
    const [imageFilterURL, setImageFilterURL] = useState<string>("");
    const [hasFilterLoaded, setHasFilterLoaded] = useState(false);

    const [openDeleteGroupMessage, setOpenDeleteGroupMessage] = useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        resetFields();
        setImageFilterURL("");
        setSelectedCategory(undefined);
        fetchCategories();
    }, [businessGroupState.currentBusinessGroup]);

    useEffect(() => {
        setIsLoading(true);

        if (categoryGroupState.categories && selectedCategory) {
            const updatedCategory = categoryGroupState.categories.find(cat => cat.Id === selectedCategory.Id);
            if (updatedCategory) {
                setSelectedCategory(updatedCategory);
                handleCategorySelect(updatedCategory.Name);
            }
        }
        setButtons(createCategoryLabels());
        setImageFilterURL(getImageURLCategoryListIcon(selectedCategory?.Id ?? "", selectedCategory?.LastModified ?? ""));

        setCategories(categoryGroupState.categories);

        setIsLoading(false);
    }, [categoryGroupState, selectedCategory]);

    const fetchCategories = () => {
        setIsLoading(true);
        const group_id = businessGroupState.currentBusinessGroup?.id ?? "";
        if (group_id) {
            dispatch(
                getCategories(group_id,(categories) => {
                if (categories){
                    console.log("Fetched");
                    // setCategories(categories);
                }
                else{
                    dispatch(fetchError("Failed to recieve categories"));
                }
                setIsLoading(false); 
            }));
        }
    };
    
    const findCategory = (key: string) => {
        if (categoryGroupState.categories) {
            return categoryGroupState.categories.find(category => category.Name === key);
        }
    };
    
    const handleCategorySelect = (key: string) =>  {
        
        setIsCreatingNew(false);

        if (categoryGroupState.categories) {
            const category = findCategory(key);
            if (category) {
                resetFields();
                
                setSelectedCategory(category);
                setName(category.Name);
                setThemeColor(category.Color);
                setMinDistance(category.MinDistanceVisible ?? null);
                setMaxDistance(category.MaxDistanceVisible ?? null);
                setAltitude(category.BeaconHeight);
                setShowDistance(category.ShowDistance);
                setTheme(category.Theme);
                
                
                setInitialValues({
                    name: category.Name,
                    themeColor: category.Color,
                    maxDistance: category.MaxDistanceVisible ?? null,
                    minDistance: category.MinDistanceVisible ?? null,
                    theme: category.Theme,
                    altitude: category.BeaconHeight,
                    showDistance: category.ShowDistance,
                });

            }
        }
    };

    const isFormChange = () => {
        return (
            name !== initialValues.name ||
            themeColor !== initialValues.themeColor ||
            maxDistance !== initialValues.maxDistance ||
            minDistance !== initialValues.minDistance ||
            altitude !== initialValues.altitude ||
            theme !== initialValues.theme ||
            showDistance !== initialValues.showDistance ||
            currentFilterFile !== null
        );
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);
        setNameError("");
    };

    const handleThemeColorChange = (newValue: string) => {
        setThemeColor(newValue);
    };

    const handleAltitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAltitude(event.target.value);
    };   
    
    const handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTheme(event.target.value);
    };

    // const handleMaxDistanceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     const inputValue = event.target.value;
    //     // Check if the input value is a valid whole number
    //     if (/^\d*$/.test(inputValue)) {
    //         setMaxDistance(inputValue);
    //         // setMaxDistanceError("");
    //     } else {
    //         // setMaxDistanceError("Please enter a valid whole number.");
    //     }
    // };
    
    //     const handleMinDistanceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     const inputValue = event.target.value;
    //     // Check if the input value is a valid whole number
    //     if (/^\d*$/.test(inputValue)) {
    //         setMinDistance(inputValue);
    //         // setMinDistanceError("");
    //     } else {
    //         // setMinDistanceError("Please enter a valid whole number.");
    //     }
    // };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputFile = event.target.files![0];
        console.log(inputFile);
        setCurrentFilterFile(inputFile);
        setFilterError("");

        if(inputFile) {
            setImageFilterURL(URL.createObjectURL(inputFile));
        }
    };

    const newCategory = () => {
        resetFields();
        setSelectedCategory(undefined);
        setIsCreatingNew(true);
    };

    const resetFields = () => {
        setName("");
        setThemeColor("#c5c5c5");
        setAltitude("High");
        setShowDistance(true);
        setTheme("ball");
        setMaxDistance(null);
        setMinDistance(null);
        setCurrentFilterFile(null);
        setFilterError("");

        setNameError("");
    };
  
      const handleImportFilter = (categoryid: string) => {
        if(currentFilterFile) {
            const group_id = businessGroupState.currentBusinessGroup?.id;
            const formData = new FormData();
            setIsLoading(true);
    
            formData.append('file', currentFilterFile, currentFilterFile.name);
    
            dispatch(uploadCategoryImage(group_id!, categoryid, formData, "FILTER" ,(result) => {
              if (result) {
                setCurrentFilterFile(null);
                setFilterError("");
              }
              else{
                setFilterError("Error Uploading Icon");
              }
              setIsLoading(false);
            }));
          }  
      };
    
    const handleModelSelect = (model: string) => {
        setTheme(model);
    };

    const handleDeleteCategory = () => {
        if (businessGroupState.currentBusinessGroup && selectedCategory) {
            setOpenDeleteGroupMessage(false);

            setIsLoading(true);

            dispatch(
                deleteCategory(businessGroupState.currentBusinessGroup.id, selectedCategory.Id ?? "", selectedCategory.Name, (result: boolean) => {
                    if (result) {
                        fetchCategories();
                        resetFields();
                    }
                })
            );
        }
    };

    const clearDistanceField = (setState: React.Dispatch<React.SetStateAction<string>>) => {
        setState("");
    };

    const clearFileField = (setState: React.Dispatch<React.SetStateAction<File | null>>, setUrl: React.Dispatch<React.SetStateAction<string>>) => {
        setState(null);
        setUrl("");
    };

    const submitSettingsForm = (event: any) => {
        event.preventDefault();

        // Required fields check

        let isValid = true;
        if (!name.trim()) {
            setNameError("Category name is required");
            isValid = false;
        }

        if (!currentFilterFile && !hasFilterLoaded) {
            setFilterError("An icon is required");
            isValid = false;
        }

        if (!isValid) return;
  
        const nameExists = categories?.some(cat => cat.Name.toUpperCase() === name.toUpperCase());
  
        if ((nameExists && isCreatingNew) || ((selectedCategory?.Name.toUpperCase() !== name.toUpperCase()) && nameExists)) {
            setNameError("A category with this name already exists");
            return;
        }
  
        if (businessGroupState.currentBusinessGroup) {
          setIsLoading(true);
          const Category: MakeCategory = {
            name: name,
            description: "",
            color: themeColor.replace("#",""),
            theme: theme,
            beaconHeight: altitude,
            showDistance: showDistance,
            maxdistancevisible: maxDistance ?? null,
            mindistancevisible: minDistance ?? null,
            BeaconGroupId: businessGroupState.currentBusinessGroup.id,
            id: selectedCategory?.Id == null ? null : selectedCategory?.Id ?? ""
          };
  
          let isExisting = false;
          if (Category.id != null) {
            isExisting = true;
          }
  
          dispatch(
            makeCategory(
              Category,
              (result: boolean, id: string | null) => {
                if(result){
                  fetchCategories();
                  if(currentFilterFile && id) {
                    handleImportFilter(id);
                  }
                  if(isExisting){
                    dispatch(showMessage("Category was updated"));
                    // setCategory(Category.name);
                    
                  }
                  else{
                    dispatch(showMessage("Category was created"));
                    resetFields();
                  }
                }
              }
            )
          );
        }
      };

    const createCategoryLabels = () => {
        const buttons: ReactElement[] = [];
        categoryGroupState.categories?.map((category) => {
            const isSelected = selectedCategory?.Name == category.Name;
            buttons.push(
                <Box key={category.Name} sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", marginTop: 2 }}>
                    <ButtonBase 
                    sx={{ 
                        borderRadius: "22px",
                        }} 
                    onClick={() => handleCategorySelect(category.Name)}>
                        <BeaconCategory 
                            category={category.Name} 
                            id={category.Id ?? ""}
                            fontSize="14px"
                            minWidth="200px"
                            borderRadius="22px"
                            padding="4px 8px"
                            justifyContent="left"
                            icons={true}
                            selected={isSelected}
                            hover={true}
                            lastModfied={category.LastModified}
                            color={category.Color}
                        />
                    </ButtonBase>
                </Box>
            );
        });
        return buttons;
    };

    const renderConfigurationMenu = () => {
        return (
            <Paper sx={{ border: '1px solid #ccc', backgroundColor: "white", borderRadius: '4px', marginTop: '16px', maxWidth:"700px", flexGrow: 1, height: "auto" }}>
                <Fade in={true} timeout={500}>
                <Box>
                <FormControl fullWidth sx={{ padding: "16px" }}>
                <Typography mb={4} mt={-2} variant="body1" fontWeight={600} fontSize={18}>Category Name</Typography>
                    <TextField 
                        id="category" 
                        value={name}
                        onChange={handleNameChange} 
                        variant="outlined" 
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        autoComplete="off"
                        spellCheck="false"
                        aria-autocomplete="none"
                        autoCorrect="off"
                        helperText={nameError ? nameError : `${name.length}/${nameLimit}`}
                        error={!!nameError}
                        slotProps={{
                            formHelperText: {
                              sx: {
                                textAlign: "right",
                              },
                            },
                        }}
                        inputProps={{ maxLength: nameLimit }}
                    />
                </FormControl>
                {/* <FormControl fullWidth sx={{ padding: "16px" }}>
                <Typography mb={4} mt={-2} variant="body1" fontWeight={600} fontSize={18}>Color</Typography>
                    <MuiColorInput
                        value={themeColor}
                        size="small"
                        format="hex"
                        isAlphaHidden={true}
                        onChange={handleThemeColorChange}
                        variant="standard"
                        sx={{
                            width: "0px",
                            '& .MuiColorInput-Button': { width: '80px', height: '80px' }
                        }}
                    />
                </FormControl> */}
                <Box sx={{ padding: '16px' }}>
                <Box display="flex" gap={35}>
                    <FormControl>
                    <Box mt={0} display="flex" flexDirection="column" alignItems="left">
                    <Typography variant="body1" textAlign="center" fontWeight={600} fontSize={18}>Icon</Typography>
                    <Box 
                        sx={{ 
                            height: "110px", 
                            display: "flex",
                            justifyContent: "center",
                        }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "80px",
                            height: "80px",
                            border: "1px black solid",
                            mt: 6,
                            backgroundColor: themeColor.startsWith("#") ? themeColor : `#${themeColor}`,
                        }}>
                        <ImageLoader    
                            imageURL={imageFilterURL}
                            onLoadStatus={setHasFilterLoaded}
                        />
                    </Box>
                    </Box>
                        <Box display="flex" alignItems="flex-start" mt={10} width="300px">
                        <TextField
                            id="upload-filter"
                            value={currentFilterFile ? currentFilterFile.name : ''}
                            size="small"
                            variant="outlined"
                            helperText={filterError || null}
                            error={!!filterError}
                            inputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "220px", height: "40px" }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton 
                                        sx={{ 
                                            padding: 1, 
                                            fontSize: "16px", 
                                            visibility: currentFilterFile ? "visible" : "hidden" }} 
                                            onClick={() => clearFileField(setCurrentFilterFile, setImageFilterURL)}>
                                                <ClearIcon sx={{ fontSize: "20px"}} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <label htmlFor="file-input-filter" style={{ marginLeft: '8px' }}>
                            <Button variant="outlined" component="span" sx={{ height: "40px" }}>
                                Browse
                            </Button>
                        </label>
                        <input
                            id="file-input-filter"
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            style={{ display: 'none' }}
                            onChange={handleFilterChange}
                        />
                        </Box>
                    </Box>
                    </FormControl>
                    <FormControl>
                    <Box mt={0} display="flex" flexDirection="column" justifyContent="center" alignItems="left">
                        <Typography variant="body1" textAlign="center" fontWeight={600} fontSize={18}>Color</Typography>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "110px" }}>
                            <Box sx={{ 
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "80px",
                                height: "80px",
                                mt: 6,
                            }}>
                            <MuiColorInput
                                value={themeColor}
                                size="small"
                                format="hex"
                                isAlphaHidden={true}
                                onChange={handleThemeColorChange}
                                variant="standard"
                                sx={{
                                    mr: 20,
                                    width: "0px",
                                    '& .MuiColorInput-Button': { width: '80px', height: '80px' }
                                }}
                            />
                            </Box>      
                        </Box>
                        </Box>
                    </FormControl>
                </Box>
                </Box>
                <FormControl fullWidth sx={{ padding: "16px" }}>
                <Typography mt={-2} variant="body1" fontWeight={600} fontSize={18}>Beacon Type</Typography>
                <Box sx={{ display: "flex", gap: 10 }}>
                    {/* <TextField
                        sx={{ padding: 2, marginTop: 3 }}
                        required
                        fullWidth
                        variant="outlined"
                        name="Theme"
                        id="theme"
                        select
                        value={theme ?? 'ball'}
                        onChange={handleThemeChange}
                    >
                    <MenuItem value={"ball"}>Ball</MenuItem>
                    <MenuItem value={"trisign"}>Tri-Sign</MenuItem>
                    <MenuItem value={"planet"}>Planet</MenuItem>
                    </TextField> */}
                    <HorizontalMenu 
                        onModelSelect={handleModelSelect}
                        model={theme}
                        experienceMode="EDIT"
                    />
                    <FormControlLabel control={<Checkbox checked={showDistance} onChange={(e) => setShowDistance(e.target.checked)} />} label="Include Distance" sx={{ whiteSpace: "nowrap" }} />
                </Box>
                </FormControl>
                <FormControl fullWidth sx={{ padding: "16px" }}>
                <Typography mt={-2} variant="body1" fontWeight={600} fontSize={18}>Beacon Height</Typography>
                    <TextField
                        sx={{ padding: 2, marginTop: 3 }}
                        required
                        fullWidth
                        variant="outlined"
                        name="altitude"
                        id="alititude"
                        select
                        value={altitude ?? 'High'}
                        onChange={handleAltitudeChange}
                        helperText={"Beacon Height controls the height above the ground beacons show up. For best experience, select Low for landscapes and High for cityscapes"}
                    >
                    <MenuItem value={"High"}>High</MenuItem>
                    <MenuItem value={"Low"}>Low</MenuItem>
                    </TextField>
                </FormControl>
                <FormControl sx={{ padding: "16px", marginBottom: "16px" }}>
                    <Typography mb={4} mt={-2} variant="body1" fontWeight={600} fontSize={18}>Visible Distance</Typography>
                    <Box display="flex" flexDirection="row" gap={20}>
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="left">
                        <Box display="flex" alignItems="end" gap={2} mt={2}>
                            <TextField
                                id="mindistance"
                                value={minDistance}
                                label="Min Distance"
                                size="small"
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: "210px" }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton 
                                            sx={{ 
                                                padding: 1, 
                                                fontSize: "16px", 
                                                visibility: minDistance ? "visible" : "hidden" }} 
                                                >
                                                    <ClearIcon sx={{ fontSize: "20px"}} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Typography variant="body1" color="#888888" fontSize={14}>meters</Typography>
                        </Box>
                        </Box>
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="left">
                        <Box display="flex" alignItems="end" gap={2} mt={2}>
                            <TextField
                                id="maxdistance"
                                value={maxDistance}
                                label="Max Distance"
                                size="small"
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: "210px" }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton sx=
                                            {{ 
                                                padding: 1, 
                                                fontSize: "16px",
                                                visibility: maxDistance ? "visible" : "hidden" 
                                            }} 
                                            >
                                                <ClearIcon  sx={{ fontSize: "20px"}}/>
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Typography variant="body1" color="#888888" fontSize={14}>meters</Typography>
                        </Box>
                        </Box>
                    </Box>
                </FormControl>
                <Divider variant="fullWidth" sx={{ }} />
                <Box display="flex" justifyContent="space-between" padding="16px">
                    <Button onClick={() => setOpenDeleteGroupMessage(true)} sx={{ color: "#FC7535", backgroundColor: AppColor.orangeBackground, padding: "0px 20px" }}>Delete</Button>
                    <Box display="flex" gap={2}>
                        <Button onClick={() => setSelectedCategory(undefined)} sx={{color: "#FC7535", backgroundColor: AppColor.orangeBackground, padding: "0px 20px"}}>Cancel</Button>
                        <Button variant="contained" onClick={submitSettingsForm} sx={{ padding: "6px 20px" }} disabled={!isFormChange()}>
                            {isCreatingNew ? "Create" : "Update"}
                        </Button>
                    </Box>
                </Box>
                </Box>
            </Fade>
            </Paper>
        );
    };

    if (isLoading || indexLoading) {
        return (
            <div
            id="scrollable-container"
            style={{ overflowY: "auto", height: "calc(100vh - 275px)" }}
            >
            <Box display="flex" flexDirection="row" gap={10}>
                <Paper sx={{ border: '1px solid #ccc', backgroundColor: "white", borderRadius: '4px', padding: '16px', marginTop: '16px', maxWidth:"300px", height: "fit-content"}}>
                        <Skeleton highlightColor={AppColor.skeletonHighlight} baseColor={AppColor.skeletonBase} style={{ display: "block", lineHeight: 1.5 }} count={4} />
                        <Divider sx={{ marginTop: 3 }} />
                        <Box mt={2}>
                            <Button sx={{ width: "200px", display: "flex", justifyContent: "flex-start" }} onClick={newCategory}>
                                <Typography fontSize={"14px"} fontWeight={"700"} color={"black"}>+ Add Category</Typography>
                            </Button>
                        </Box>
                    </Paper>
                    <Paper 
                    sx={{ 
                    height: "700px", 
                    maxWidth: "700px", 
                    border: '1px solid #ccc',
                    backgroundColor: "white", 
                    borderRadius: '4px', 
                    padding: '16px', 
                    marginTop: '16px',  
                    flexGrow: 1, 
                    }}> 
                    <Typography display="flex" justifyContent="center" alignItems="center" marginTop="50%" variant="body1" color="#cbcbcb"> Select a Category</Typography> 
                </Paper>
            </Box>
            </div>
        );
    } else {
        return (
            <div
            id="scrollable-container"
            style={{ overflowY: "auto", height: "calc(100vh - 275px)" }}
            >
            <Box display="flex" flexDirection="row" gap={10}>
                <Paper sx={{ border: '1px solid #ccc', backgroundColor: "white", borderRadius: '4px', padding: '16px', marginTop: '16px', maxWidth:"235px", height: "fit-content"}}>
                    {buttons}
                    <Divider sx={{ marginTop: 3 }} />
                    <Box mt={2}>
                        <Button sx={{ width: "200px", display: "flex", justifyContent: "flex-start" }} onClick={newCategory}>
                            <Typography fontSize={"14px"} fontWeight={"700"} color={"black"}>+ Add Category</Typography>
                        </Button>
                    </Box>
                </Paper>
                {selectedCategory || isCreatingNew ? renderConfigurationMenu() 
                    : 
                    <Paper 
                        sx={{ 
                        height: "767px", 
                        maxWidth: "700px", 
                        border: '1px solid #ccc',
                        backgroundColor: "white", 
                        borderRadius: '4px', 
                        padding: '16px', 
                        marginTop: '16px',  
                        flexGrow: 1, 
                        }}> 
                        <Typography display="flex" justifyContent="center" alignItems="center" marginTop="50%" variant="body1" color="#cbcbcb"> Select a Category</Typography> 
                    </Paper>
                }
            <AppConfirmDialog
                open={openDeleteGroupMessage}
                onConfirm={() => handleDeleteCategory()}
                onDeny={() => {
                setOpenDeleteGroupMessage(false);
                }}
                title="Are you sure you want to delete category: "
                content={selectedCategory?.Name + "?"}
                dialogTitle="Delete Category"
            />
            </Box>
            </div>
        );
    }

};

export default Categories;
